import {
  DateBasedSalary,
  SalaryDateSpec,
  TargetedShiftSalaryRow,
  TargetedShiftSpec,
  TargetingKeys,
  TargetingKeysCmd,
} from "./targetedShiftSalariesApi";

const PART_SEP = "\u241F";
const ID_SEP = "\u241E";

const toIdName = (value: unknown): { id: string; name: string } => {
  if (value && typeof value === "object" && "id" in value) {
    const item = value as { id: string; name?: string };
    return { id: item.id, name: item.name || "" };
  }
  return { id: String(value ?? ""), name: "" };
};

/**
 * Employment-by-id returns targeting keys as ids (`custCompanyId`, string lists).
 * empTargetedShiftSpec / the UI table expect `{ id, name }` objects.
 */
export const toNamedTargetingKeys = (keys: unknown): TargetingKeys => {
  const raw = keys as
    | {
        custCompany?: { id: string; name?: string };
        custCompanyId?: string;
        custOrgs?: unknown[];
        workLocs?: unknown[];
        workTitles?: string[];
      }
    | undefined;
  return {
    custCompany: raw?.custCompany
      ? { id: raw.custCompany.id, name: raw.custCompany.name || "" }
      : { id: raw?.custCompanyId || "", name: "" },
    custOrgs: (raw?.custOrgs ?? []).map(toIdName),
    workLocs: (raw?.workLocs ?? []).map(toIdName),
    workTitles: raw?.workTitles ?? [],
  };
};

export const normalizeSalaryRows = (
  salaries: TargetedShiftSalaryRow[] | undefined,
): TargetedShiftSalaryRow[] =>
  (salaries ?? []).map((row) => ({
    ...row,
    keys: toNamedTargetingKeys(row.keys),
  }));

export const buildSalaryKey = (keys: TargetingKeys): string => {
  const sorted = {
    custOrgs: [...keys.custOrgs.map((o) => o.id)].sort(),
    workLocs: [...keys.workLocs.map((wl) => wl.id)].sort(),
    workTitles: [...keys.workTitles].sort(),
  };
  return [
    keys.custCompany.id,
    sorted.custOrgs.join(ID_SEP),
    sorted.workLocs.join(ID_SEP),
    sorted.workTitles.join(ID_SEP),
  ].join(PART_SEP);
};

export const toCommandKeys = (keys: TargetingKeys): TargetingKeysCmd => ({
  custCompanyId: keys.custCompany.id,
  custOrgs: [...keys.custOrgs.map((o) => o.id)].sort(),
  workLocs: [...keys.workLocs.map((wl) => wl.id)].sort(),
  workTitles: [...keys.workTitles].sort(),
});

export const getActiveSalaryRange = (
  salary: DateBasedSalary,
  date: Date,
): SalaryDateSpec | undefined => {
  const isoDate = date.toISOString().slice(0, 10);
  return salary.ranges.filter((r) => r.from <= isoDate).at(-1);
};

export const hasMultipleDateRanges = (salary: DateBasedSalary): boolean =>
  salary.ranges.length > 1;

export const isDuplicateKeys = (
  a: TargetingKeys,
  b: TargetingKeys,
): boolean => {
  const sameIdList = (x: { id: string }[], y: { id: string }[]) => {
    if (x.length !== y.length) return false;
    const xs = x.map((o) => o.id).sort();
    const ys = y.map((o) => o.id).sort();
    return xs.every((v, i) => v === ys[i]);
  };
  const sameStringList = (x: string[], y: string[]) => {
    if (x.length !== y.length) return false;
    const xs = [...x].sort();
    const ys = [...y].sort();
    return xs.every((v, i) => v === ys[i]);
  };
  return (
    a.custCompany.id === b.custCompany.id &&
    sameIdList(a.custOrgs, b.custOrgs) &&
    sameIdList(a.workLocs, b.workLocs) &&
    sameStringList(a.workTitles, b.workTitles)
  );
};

const intersects = (a: string[], b: string[]): boolean =>
  a.length === 0 || b.length === 0 || a.some((id) => b.includes(id));

const dimensionsScore = (keys: TargetingKeys): number =>
  (keys.custOrgs.length > 0 ? 1 : 0) +
  (keys.workLocs.length > 0 ? 1 : 0) +
  (keys.workTitles.length > 0 ? 1 : 0);

const totalKeyCount = (keys: TargetingKeys): number =>
  keys.custOrgs.length + keys.workLocs.length + keys.workTitles.length;

export const hasAmbiguousOverlap = (
  a: TargetingKeys,
  b: TargetingKeys,
): boolean => {
  if (a.custCompany.id !== b.custCompany.id) return false;
  if (isDuplicateKeys(a, b)) return false;
  if (dimensionsScore(a) !== dimensionsScore(b)) return false;
  if (totalKeyCount(a) !== totalKeyCount(b)) return false;
  return (
    intersects(
      a.custOrgs.map((o) => o.id),
      b.custOrgs.map((o) => o.id),
    ) &&
    intersects(
      a.workLocs.map((l) => l.id),
      b.workLocs.map((l) => l.id),
    ) &&
    intersects(a.workTitles, b.workTitles)
  );
};

export const findAmbiguousOverlap = (
  keys: TargetingKeys,
  rows: TargetedShiftSalaryRow[],
): TargetedShiftSalaryRow | undefined =>
  rows.find((row) => hasAmbiguousOverlap(row.keys, keys));

export const mergeSalaryRange = (
  existing: DateBasedSalary,
  newSpec: SalaryDateSpec,
): DateBasedSalary => {
  const withoutDate = existing.ranges.filter((r) => r.from !== newSpec.from);
  const merged = [...withoutDate, newSpec].sort((a, b) =>
    a.from.localeCompare(b.from),
  );
  return { ranges: merged };
};

export const removeSalaryRange = (
  existing: DateBasedSalary,
  date: string,
): DateBasedSalary => {
  const ranges = existing.ranges.filter((r) => r.from !== date);
  return { ranges };
};

export const getExistingSalary = (
  spec: TargetedShiftSpec | null | undefined,
  keys: TargetingKeys,
): DateBasedSalary | undefined =>
  spec?.salaries.find((row) => isDuplicateKeys(row.keys, keys))?.salary;
